/**
 * PatternSelector - Visual grid selector for pattern axis values
 *
 * Displays patterns with icons and translated names in a grid layout,
 * exactly like the develop branch VariantsCard pattern selector.
 * Shows confirmation dialog when deselecting patterns that have variants.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import type { VariantV3, AxisValue } from '../../types';
import { isDefaultAxisValue } from '../../types';
import { VariantImpactDialog } from './VariantImpactDialog';
import { normalizePatternName } from '../../../../../common/utils/pattern-name';

// Pattern icons
import BrickBondIcon from '../../../../../assets/icons/products/patterns/brick-bond-icon.png';
import HerringboneIcon from '../../../../../assets/icons/products/patterns/herringbone-icon.png';
import ChevronIcon from '../../../../../assets/icons/products/patterns/chevron-icon.png';
import StackBondIcon from '../../../../../assets/icons/products/patterns/stack-bond-icon.png';
import OpusIcon from '../../../../../assets/icons/products/patterns/opus-icon.png';
import CabochonIcon from '../../../../../assets/icons/products/patterns/cabochon-icon.png';
import HexagonIcon from '../../../../../assets/icons/products/patterns/hexagon-icon.png';
import FishScaleIcon from '../../../../../assets/icons/products/patterns/fish-scale-icon.png';
import ArabesqueIcon from '../../../../../assets/icons/products/patterns/arabesque-icon.png';
import AlureIcon from '../../../../../assets/icons/products/patterns/alure-icon.png';
import ArrowIcon from '../../../../../assets/icons/products/patterns/arrow-icon.png';
import PennyIcon from '../../../../../assets/icons/products/patterns/penny-icon.png';
import TriangleIcon from '../../../../../assets/icons/products/patterns/triangle-icon.png';
import FlagstoneIcon from '../../../../../assets/icons/products/patterns/flagstone-icon.png';
import FlagstoneTightIcon from '../../../../../assets/icons/products/patterns/flagstone-tight-icon.png';
import WaveIcon from '../../../../../assets/icons/products/patterns/wave-icon.png';
import LongHexIcon from '../../../../../assets/icons/products/patterns/longhex-icon.png';
import AlternatingIcon from '../../../../../assets/icons/products/patterns/alternating-icon.png';

// =============================================================================
// Types
// =============================================================================

interface PatternSelectorProps {
  /** Available pattern values (from predefinedValues) */
  availablePatterns: string[];
  /** Currently selected patterns */
  selectedPatterns: string[];
  /** Change handler */
  onChange: (patterns: string[]) => void;
  /** Disabled state */
  disabled?: boolean;
  /** All variants (used to find affected variants when deselecting patterns) */
  variants?: VariantV3[];
  /** Callback to delete variants by their UUIDs */
  onDeleteVariants?: (uuids: string[]) => void;
  /** Callback to assign a value to all variants with null for the pattern axis */
  onAssignAxisValueToVariants?: (axisName: string, value: AxisValue) => void;
}

export interface PatternInfo {
  value: string;
  label: string;
  icon: string;
}

/** Represents a pending deletion with the pattern and affected variants */
interface PendingDeletion {
  patternValue: string;
  storedValue: string;
  affectedVariants: VariantV3[];
}

/** Represents a pending assignment of a pattern to variants with null pattern */
interface PendingAssignment {
  patternValue: string;
  affectedVariants: VariantV3[];
}

// =============================================================================
// Pattern Configuration (matching develop branch)
// =============================================================================

export const ALL_PATTERNS: PatternInfo[] = [
  { value: 'bricks', label: 'Brick Bond', icon: BrickBondIcon },
  { value: 'herringbone', label: 'Herringbone', icon: HerringboneIcon },
  { value: 'chevron', label: 'Chevron', icon: ChevronIcon },
  { value: 'stackbond', label: 'Stack Bond', icon: StackBondIcon },
  { value: 'opus', label: 'Opus', icon: OpusIcon },
  { value: 'cabochon', label: 'Cabochon', icon: CabochonIcon },
  { value: 'hexagonal', label: 'Hexagonal', icon: HexagonIcon },
  { value: 'fishscale', label: 'Fish Scale', icon: FishScaleIcon },
  { value: 'arabesque', label: 'Arabesque', icon: ArabesqueIcon },
  { value: 'alure', label: 'Alure', icon: AlureIcon },
  { value: 'arrow', label: 'Arrow', icon: ArrowIcon },
  { value: 'penny', label: 'Penny Round', icon: PennyIcon },
  { value: 'triangle', label: 'Triangle', icon: TriangleIcon },
  { value: 'flagstone', label: 'Flagstone', icon: FlagstoneIcon },
  { value: 'flagstonetight', label: 'Flagstone (tight)', icon: FlagstoneTightIcon },
  { value: 'wave', label: 'Wave', icon: WaveIcon },
  { value: 'longhex', label: 'Long Hexagon', icon: LongHexIcon },
  { value: 'alternating', label: 'Alternating', icon: AlternatingIcon },
];

// =============================================================================
// Main Component
// =============================================================================

export const PatternSelector: React.FC<PatternSelectorProps> = ({
  availablePatterns,
  selectedPatterns,
  onChange,
  disabled = false,
  variants = [],
  onDeleteVariants,
  onAssignAxisValueToVariants,
}) => {
  const { t } = useTranslation();

  // Pending deletion state for confirmation dialog
  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null);

  // Pending assignment state for "assign to existing variants" dialog
  const [pendingAssignment, setPendingAssignment] = useState<PendingAssignment | null>(null);

  // Filter ALL_PATTERNS to only show patterns available for the selected product type
  const patternsForDisplay = useMemo(() => {
    if (!availablePatterns || availablePatterns.length === 0) return ALL_PATTERNS;
    // Through the shared pattern names, so an older family's 'brick-bond',
    // 'stacked' or 'standard' offers the Bricks and Stack Bond tiles (ARDE-293).
    const normalizedAvailable = availablePatterns.map((p) => normalizePatternName(p));
    return ALL_PATTERNS.filter(p => normalizedAvailable.includes(p.value));
  }, [availablePatterns]);

  // Normalize pattern value for comparison ("stack bond", "stacked", "Brick bond" → the tile's value)
  const normalizePattern = (p: string) => normalizePatternName(p) ?? '';

  // Find matching selected pattern (handles variations like "stackbond" vs "Pose en pile")
  const findSelectedPattern = useCallback(
    (patternValue: string): string | null => {
      const normalizedPatternValue = normalizePattern(patternValue);
      return (
        selectedPatterns.find(
          (sp) => normalizePattern(sp) === normalizedPatternValue || sp === patternValue
        ) || null
      );
    },
    [selectedPatterns]
  );

  // Check if pattern is selected
  const isPatternSelected = useCallback(
    (patternValue: string) => {
      return findSelectedPattern(patternValue) !== null;
    },
    [findSelectedPattern]
  );

  // Find variants that use a specific pattern
  const getAffectedVariants = useCallback(
    (patternValue: string): VariantV3[] => {
      const normalizedPatternValue = normalizePattern(patternValue);
      return variants.filter((variant) => {
        const variantPattern = variant.axes?.pattern;
        if (variantPattern === undefined || variantPattern === null) return false;
        const variantPatternStr = typeof variantPattern === 'string' ? variantPattern : '';
        return (
          normalizePattern(variantPatternStr) === normalizedPatternValue ||
          variantPatternStr === patternValue
        );
      });
    },
    [variants]
  );

  // Find selected variants with null for the pattern axis (for assignment popup)
  const getVariantsWithNullPattern = useCallback((): VariantV3[] => {
    return variants.filter(v => v.isSelected && isDefaultAxisValue(v.axes?.pattern));
  }, [variants]);

  // Confirm assignment - assigns the pattern to variants, then adds it to the axis
  const handleConfirmAssignment = useCallback(() => {
    if (!pendingAssignment) return;
    if (onAssignAxisValueToVariants) {
      onAssignAxisValueToVariants('pattern', pendingAssignment.patternValue);
    }
    onChange([...selectedPatterns, pendingAssignment.patternValue]);
    setPendingAssignment(null);
  }, [pendingAssignment, onAssignAxisValueToVariants, onChange, selectedPatterns]);

  // Cancel assignment
  const handleCancelAssignment = useCallback(() => {
    setPendingAssignment(null);
  }, []);

  const handlePatternToggle = useCallback(
    (patternValue: string) => {
      if (disabled) return;

      const existingPattern = findSelectedPattern(patternValue);
      if (existingPattern) {
        // Trying to deselect - check for affected variants
        const affectedVariants = getAffectedVariants(patternValue);
        if (affectedVariants.length > 0) {
          // Show confirmation dialog
          setPendingDeletion({
            patternValue,
            storedValue: existingPattern,
            affectedVariants,
          });
        } else {
          // No variants affected, remove directly
          onChange(selectedPatterns.filter((p) => p !== existingPattern));
        }
      } else {
        // Intercept: first pattern for an empty optional axis with existing variants
        if (selectedPatterns.length === 0 && onAssignAxisValueToVariants) {
          const nullVariants = getVariantsWithNullPattern();
          if (nullVariants.length > 0) {
            setPendingAssignment({ patternValue, affectedVariants: nullVariants });
            return;
          }
        }
        // Add the new pattern
        onChange([...selectedPatterns, patternValue]);
      }
    },
    [disabled, findSelectedPattern, getAffectedVariants, onChange, selectedPatterns, onAssignAxisValueToVariants, getVariantsWithNullPattern]
  );

  // Confirm deletion - removes the pattern AND deletes affected variants
  const handleConfirmDeletion = useCallback(() => {
    if (!pendingDeletion) return;

    // Delete affected variants first
    if (onDeleteVariants && pendingDeletion.affectedVariants.length > 0) {
      const uuidsToDelete = pendingDeletion.affectedVariants.map((v) => v.uuid);
      onDeleteVariants(uuidsToDelete);
    }

    // Then remove the pattern
    onChange(selectedPatterns.filter((p) => p !== pendingDeletion.storedValue));

    // Close dialog
    setPendingDeletion(null);
  }, [pendingDeletion, onChange, selectedPatterns, onDeleteVariants]);

  // Cancel deletion
  const handleCancelDeletion = useCallback(() => {
    setPendingDeletion(null);
  }, []);

  // Get pattern label (translated)
  const getPatternLabel = useCallback(
    (patternValue: string): string => {
      return t(`PATTERNS.${patternValue.toUpperCase().replace(' ', '_')}`);
    },
    [t]
  );

  return (
    <>
      {/* Wraps into rows: one scrolling row pushed the card past the page once
          there were more patterns than fit across. */}
      <div className="flex flex-wrap gap-2.5">
        {patternsForDisplay.map((pattern) => {
          const isSelected = isPatternSelected(pattern.value);

          return (
            <button
              key={pattern.value}
              type="button"
              data-slot="pattern-tile"
              aria-pressed={isSelected}
              disabled={disabled}
              onClick={() => handlePatternToggle(pattern.value)}
              className={cn(
                'flex shrink-0 items-center gap-2 border px-3 py-2 transition-colors select-none',
                'disabled:cursor-not-allowed disabled:opacity-60',
                isSelected
                  ? 'border-foreground bg-muted'
                  : 'border-border bg-transparent hover:bg-muted/60'
              )}
            >
              {/* Pattern icon */}
              <img
                src={pattern.icon}
                alt={pattern.label}
                className={cn(
                  'size-7 shrink-0 object-contain transition-opacity dark:invert',
                  isSelected ? 'opacity-100' : 'opacity-50'
                )}
              />

              {/* Pattern label - translated */}
              <span
                className={cn(
                  'text-xs font-medium whitespace-nowrap capitalize',
                  isSelected ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {getPatternLabel(pattern.value)}
              </span>

              {/* Soft check icon when selected */}
              {isSelected && (
                <Check data-slot="pattern-check" className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>

      {/* Confirmation dialog for deselecting patterns with affected variants */}
      <VariantImpactDialog
        open={!!pendingDeletion}
        onCancel={handleCancelDeletion}
        onConfirm={handleConfirmDeletion}
        destructive
        variants={pendingDeletion?.affectedVariants || []}
        title={t('PAGES.NEW_PRODUCT.V3.DELETE_PATTERN_TITLE', { defaultValue: 'Remove pattern?' })}
        message={t('PAGES.NEW_PRODUCT.V3.DELETE_PATTERN_MESSAGE', {
          pattern: pendingDeletion ? getPatternLabel(pendingDeletion.patternValue) : '',
          count: pendingDeletion?.affectedVariants.length || 0,
          defaultValue: `Removing "${pendingDeletion ? getPatternLabel(pendingDeletion.patternValue) : ''}" will also delete ${pendingDeletion?.affectedVariants.length || 0} variant(s):`,
        })}
        confirmLabel={t('DELETE', { defaultValue: 'Delete' })}
      />

      {/* Assignment dialog for assigning first pattern to variants with null pattern */}
      <VariantImpactDialog
        open={!!pendingAssignment}
        onCancel={handleCancelAssignment}
        onConfirm={handleConfirmAssignment}
        variants={pendingAssignment?.affectedVariants || []}
        title={t('PAGES.NEW_PRODUCT.V3.ASSIGN_VALUE_TITLE', { defaultValue: 'Assign to existing variants?' })}
        message={t('PAGES.NEW_PRODUCT.V3.ASSIGN_PATTERN_MESSAGE', {
          count: pendingAssignment?.affectedVariants.length || 0,
          pattern: pendingAssignment ? getPatternLabel(pendingAssignment.patternValue) : '',
          defaultValue: `There are ${pendingAssignment?.affectedVariants.length || 0} existing variant(s) without a pattern. Do you want to assign "${pendingAssignment ? getPatternLabel(pendingAssignment.patternValue) : ''}" to them?`,
        })}
        confirmLabel={t('PAGES.NEW_PRODUCT.V3.ASSIGN_BUTTON', { defaultValue: 'Assign' })}
      />
    </>
  );
};

export default PatternSelector;
